import { ControlledDropDown, NAMESPACE } from '@ui-kit'
import { LookupComboboxProps } from '../types/formRenderer.types'
import { FieldValues, Path, PathValue } from 'react-hook-form'
import { useEffect, useState } from 'react'
import {
  DataTypeStore,
  DropdownType,
  hideLoader,
  SelectedField,
  showLoader,
} from '@app-store'
import { useAppDispatch } from '../utils/agent.store'
import { ElementType } from '../types/layout.types'
import { generateLabelFromSelectedFields } from '../utils/listing.utils'
import { getEntityDataEmbedded } from '../apis/entityData.api'
import { hasPermission } from '../utils/permission'
import { MODE_CONSTANTS } from '../utils/constants'

const LookupCombobox = <T extends FieldValues>({
  element,
  errors,
  control,
  setValue,
  dataQaId,
  ...rest
}: LookupComboboxProps<T>) => {
  const [optionData, setOptionData] = useState<DropdownType[]>([])
  const [searchResultOption, setSearchResultOption] = useState<DropdownType[]>(
    []
  )

  const dispatch = useAppDispatch()

  const fillDropDownOptions = async (
    entityId: number,
    entityName: string | null,
    fields: SelectedField[]
  ) => {
    try {
      dispatch(showLoader())
      if (!hasPermission(MODE_CONSTANTS.VIEW, entityId)) {
        setOptionData([])
        return
      }
      const res = await getEntityDataEmbedded(
        entityName,
        entityId,
        fields.map(({ name }) => name)
      )
      const data: DataTypeStore[] = res?.data?.content ?? []
      for (const field of fields) {
        if (!hasPermission(MODE_CONSTANTS.VIEW, entityId, field.id))
          data.forEach((row) => (row[field.name] = NAMESPACE.REDACTED))
      }

      const options = data.map((d) => {
        return {
          label: generateLabelFromElementAndDataFromAPI(element, d),
          value: generateLabelFromElementAndDataFromAPI(element, d),
        }
      })
      setOptionData(options)
      setSearchResultOption(options)
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const generateLabelFromElementAndDataFromAPI = (
    element: ElementType,
    data?: DataTypeStore
  ) => {
    return generateLabelFromSelectedFields(
      element.meta.selected_fields ?? [],
      data ?? {},
      element?.meta?.relationship?.target_entity_id ?? ''
    )
  }

  useEffect(() => {
    if (element.meta.source_id && element.meta.selected_fields) {
      fillDropDownOptions(
        element.meta.source_id,
        null,
        element.meta.selected_fields ?? []
      )
    }
  }, [])

  return (
    <div data-qa-id={dataQaId?? ''}>
      <ControlledDropDown
        isCombobox
        isCustomSearch
        isSearchable
        name={element.keyName as Path<T>}
        tabIndex={rest?.tabIndex}
        errors={errors}
        label={element.meta.label}
        control={control}
        onChange={(e) => {
          setValue(element.keyName as Path<T>, e.value as PathValue<T, Path<T>>)
        }}
        onSearchChange={(search) => {
          const selectOptions = optionData.filter((option) =>
            option?.label?.toLowerCase()?.includes(search)
          )
          setSearchResultOption(selectOptions || [])
          setValue(element.keyName as Path<T>, search as PathValue<T, Path<T>>)
        }}
        options={searchResultOption}
        {...rest}
      />

      {}
    </div>
  )
}

export default LookupCombobox
